import { dayKey, hoursToMinutes, nomsDesSites } from "utils";

// Indicateurs du dashboard, calculés à partir des forfaits, interventions et clients déjà chargés.
// Fonctions pures (la date du jour est passée en paramètre) pour pouvoir les tester.

type FirestoreDate = { seconds: number; nanoseconds: number } | Date;

export type RenewStatus = "Épuisé" | "< 1h";

export type ForfaitToRenewRow = {
  id: string;
  name: string;
  status: RenewStatus;
  lastIntervention: Date | null;
  progress: number;
};

export type EmailToSendRow = {
  id: string;
  client: string;
  site: string;
  date: Date;
};

export type DashboardData = {
  kpis: {
    renewAmount: number;
    renewCount: number;
    activeRevenue: number;
    activeCount: number;
    remainingHours: number;
    interventionsThisMonth: number;
    interventionsLastMonth: number;
  };
  revenue: {
    labels: string[];
    current: number[];
    previous: number[];
    total: number;
    previousTotal: number;
  };
  hours: {
    labels: string[];
    hourly: number[];
    annual: number[];
    total: number;
  };
  lowestRemaining: {
    labels: string[];
    remaining: number[];
    lowCount: number;
  };
  health: {
    healthy: number;
    watch: number;
    critical: number;
  };
  toRenew: ForfaitToRenewRow[];
  emailsToSend: {
    rows: EmailToSendRow[];
    total: number;
  };
  interventionDays: Set<string>;
};

export const MONTHS_SHOWN = 12;
export const LOWEST_REMAINING_SHOWN = 7;
export const EMAILS_WINDOW_DAYS = 90;
export const EMAILS_SHOWN = 5;

// Email d'intervention à envoyer : pas encore parti, sur la période récente
export function isEmailPending(envoiEmail: boolean, date: Date, now: Date) {
  return !envoiEmail && date.getTime() >= now.getTime() - EMAILS_WINDOW_DAYS * 24 * 60 * 60 * 1000;
}

export function toDate(value: FirestoreDate): Date {
  if (value instanceof Date) {
    return value;
  }
  return new Date(value.seconds * 1000 + value.nanoseconds / 1000000);
}

export function monthKey(date: Date) {
  return `${date.getFullYear()}-${date.getMonth()}`;
}

// Les `count` derniers mois, du plus ancien au mois en cours, décalés de `offsetMonths`
function lastMonths(now: Date, count: number, offsetMonths = 0) {
  return Array.from({ length: count }, (_, i) => {
    return new Date(now.getFullYear(), now.getMonth() - offsetMonths - (count - 1 - i), 1);
  });
}

function monthLabel(date: Date) {
  return date.toLocaleDateString("fr-FR", { month: "short" }).replace(".", "");
}

function sum(values: number[]) {
  return values.reduce((acc, value) => acc + value, 0);
}

export function computeDashboard(
  forfaits: ForfaitType[],
  interventions: InterventionType[],
  clients: ClientType[],
  now: Date = new Date(),
  sites: SiteType[] = []
): DashboardData {
  const clientNames = new Map(clients.map((client) => [client.id, client.raison_sociale]));
  const forfaitsById = new Map(forfaits.map((forfait) => [forfait.id, forfait]));

  // Temps passé par forfait
  const tempsPasse = new Map<string, number>();
  interventions.forEach((intervention) => {
    tempsPasse.set(
      intervention.idforfait,
      (tempsPasse.get(intervention.idforfait) || 0) + (intervention.temps_passe || 0)
    );
  });

  // Interventions datées : un document sans date_intervention compte dans le temps passé
  // mais est ignoré par les indicateurs datés
  const datees = interventions
    .filter((intervention) => intervention.date_intervention)
    .map((intervention) => ({ intervention, date: toDate(intervention.date_intervention) }));

  // Dernière intervention par forfait
  const lastIntervention = new Map<string, Date>();
  datees.forEach(({ intervention, date }) => {
    const last = lastIntervention.get(intervention.idforfait);
    if (!last || date > last) {
      lastIntervention.set(intervention.idforfait, date);
    }
  });

  const actifs = forfaits.filter((forfait) => forfait.statut === true);
  // Forfaits horaires (type 1) actifs : les seuls qui ont un budget d'heures
  const horaires = actifs
    .filter((forfait) => forfait.type === 1)
    .map((forfait) => {
      const temps = forfait.temps || 0;
      const remaining = temps - (tempsPasse.get(forfait.id) || 0);
      const consumed = temps > 0 ? ((temps - remaining) / temps) * 100 : 100;
      return {
        forfait,
        remaining,
        remainingMinutes: hoursToMinutes(remaining),
        progress: Math.min(Math.max(Math.round(consumed), 0), 100),
      };
    });

  // Nom affiché : le client, suivi du site s'il a plusieurs forfaits horaires actifs
  const forfaitsParClient = new Map<string, number>();
  horaires.forEach(({ forfait }) => {
    forfaitsParClient.set(forfait.idclient, (forfaitsParClient.get(forfait.idclient) || 0) + 1);
  });
  const forfaitName = (forfait: ForfaitType) => {
    const client = clientNames.get(forfait.idclient) || "Client inconnu";
    const site = nomsDesSites(forfait.idsites, sites) || forfait.site_internet;
    return forfaitsParClient.get(forfait.idclient) > 1 && site ? `${client} (${site})` : client;
  };

  const lowTime = horaires.filter(({ remainingMinutes }) => remainingMinutes <= 60);

  // Interventions de ce mois et du mois précédent
  const thisMonth = monthKey(now);
  const previousMonth = monthKey(new Date(now.getFullYear(), now.getMonth() - 1, 1));
  const interventionsByMonth = new Map<string, number>();
  datees.forEach(({ date }) => {
    const key = monthKey(date);
    interventionsByMonth.set(key, (interventionsByMonth.get(key) || 0) + 1);
  });

  // Chiffre d'affaires par mois de début de forfait : 12 derniers mois et mêmes mois un an plus tôt
  const revenueByMonth = new Map<string, number>();
  forfaits.forEach((forfait) => {
    if (!forfait.date_debut) {
      return;
    }
    const key = monthKey(toDate(forfait.date_debut));
    revenueByMonth.set(key, (revenueByMonth.get(key) || 0) + (forfait.prix || 0));
  });
  const months = lastMonths(now, MONTHS_SHOWN);
  const previousMonths = lastMonths(now, MONTHS_SHOWN, 12);
  const revenueCurrent = months.map((month) => revenueByMonth.get(monthKey(month)) || 0);
  const revenuePrevious = previousMonths.map((month) => revenueByMonth.get(monthKey(month)) || 0);

  // Heures d'intervention par mois, selon le type du forfait (annuel ou horaire)
  const hourlyByMonth = new Map<string, number>();
  const annualByMonth = new Map<string, number>();
  datees.forEach(({ intervention, date }) => {
    const key = monthKey(date);
    const target = forfaitsById.get(intervention.idforfait)?.type === 2 ? annualByMonth : hourlyByMonth;
    target.set(key, (target.get(key) || 0) + (intervention.temps_passe || 0));
  });
  const hourly = months.map((month) => hourlyByMonth.get(monthKey(month)) || 0);
  const annual = months.map((month) => annualByMonth.get(monthKey(month)) || 0);

  // Forfaits les plus proches de l'épuisement
  const lowest = [...horaires]
    .sort((a, b) => a.remaining - b.remaining)
    .slice(0, LOWEST_REMAINING_SHOWN);

  // État des forfaits horaires actifs selon la part d'heures restante
  const health = { healthy: 0, watch: 0, critical: 0 };
  horaires.forEach(({ forfait, remaining }) => {
    const remainingShare = forfait.temps > 0 ? remaining / forfait.temps : 0;
    if (remainingShare > 0.5) {
      health.healthy += 1;
    } else if (remainingShare >= 0.1) {
      health.watch += 1;
    } else {
      health.critical += 1;
    }
  });

  // Forfaits à renouveler (même liste que le chiffre clé), du plus au moins consommé
  const toRenew: ForfaitToRenewRow[] = [...lowTime]
    .sort((a, b) => b.progress - a.progress || a.remaining - b.remaining)
    .map(({ forfait, remainingMinutes, progress }) => ({
      id: forfait.id,
      name: forfaitName(forfait),
      status: remainingMinutes <= 0 ? "Épuisé" : "< 1h",
      lastIntervention: lastIntervention.get(forfait.id) || null,
      progress,
    }));

  // Emails d'intervention non envoyés sur la période récente
  const unsent = datees
    .filter(({ intervention, date }) => isEmailPending(intervention.envoi_email, date, now))
    .sort((a, b) => b.date.getTime() - a.date.getTime());

  return {
    kpis: {
      renewAmount: sum(lowTime.map(({ forfait }) => forfait.prix || 0)),
      renewCount: lowTime.length,
      activeRevenue: sum(actifs.map((forfait) => forfait.prix || 0)),
      activeCount: actifs.length,
      remainingHours: sum(horaires.map(({ remaining }) => Math.max(remaining, 0))),
      interventionsThisMonth: interventionsByMonth.get(thisMonth) || 0,
      interventionsLastMonth: interventionsByMonth.get(previousMonth) || 0,
    },
    revenue: {
      labels: months.map(monthLabel),
      current: revenueCurrent,
      previous: revenuePrevious,
      total: sum(revenueCurrent),
      previousTotal: sum(revenuePrevious),
    },
    hours: {
      labels: months.map(monthLabel),
      hourly,
      annual,
      total: sum(hourly) + sum(annual),
    },
    lowestRemaining: {
      labels: lowest.map(({ forfait }) => forfaitName(forfait)),
      remaining: lowest.map(({ remaining }) => remaining),
      lowCount: lowTime.length,
    },
    health,
    toRenew,
    emailsToSend: {
      rows: unsent.slice(0, EMAILS_SHOWN).map(({ intervention, date }) => {
        const forfait = forfaitsById.get(intervention.idforfait);
        return {
          id: intervention.id,
          client: (forfait && clientNames.get(forfait.idclient)) || "Client inconnu",
          site: intervention.site_internet,
          date,
        };
      }),
      total: unsent.length,
    },
    interventionDays: new Set(datees.map(({ date }) => dayKey(date))),
  };
}

// Variation en % entre deux valeurs (null si la valeur de référence est nulle)
export function variation(current: number, previous: number): number | null {
  if (!previous) {
    return null;
  }
  return Math.round(((current - previous) / previous) * 100);
}

export const formatEuros = (value: number) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(value);
